import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PriceTag = ({ orig, pay }) => pay < orig ? (
  <span className="block mt-1">
    <s className="text-white/40 text-sm">${(orig / 100).toFixed(2)}</s>{" "}
    <b className="till-accent font-display">${(pay / 100).toFixed(2)}</b>{" "}
    <span className="text-[#9EF01A] text-xs font-bold">save ${((orig - pay) / 100).toFixed(2)}</span>
  </span>
) : <b className="block mt-1 till-accent font-display">${(orig / 100).toFixed(2)}</b>;

// Price-poster purchase: QR points here. Registration (waiver) is required before payment.
export default function BuyPage() {
  const { itemId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState(params.get("name") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [phone, setPhone] = useState(params.get("phone") || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [promo, setPromo] = useState(null); // resolved code | false (invalid) | null
  const [upgraded, setUpgraded] = useState(false);
  const [addOnline, setAddOnline] = useState(false);

  const isCart = itemId === "cart";
  const iParam = params.get("i") || "";
  const cartLines = !isCart ? [] : iParam.split("~").filter(Boolean)
    .map((s) => { const [id, q] = s.split("."); return { id, qty: Math.max(1, parseInt(q, 10) || 1) }; })
    .map((l) => ({ ...l, item: allItems.find((it) => it.id === l.id) })).filter((l) => l.item);
  const cartTotal = cartLines.reduce((s, l) => s + l.item.price_cents * l.qty, 0);

  // ----- upsell + pricing (single-item flow) -----
  const onlineItem = allItems.length ? allItems[allItems.length - 1] : null;
  const upgrade = item ? [...allItems].filter((x) => x.price_cents > item.price_cents && x.id !== item.id && x.id !== onlineItem?.id)
    .sort((a, b) => a.price_cents - b.price_cents)[0] : null;
  const mainItem = upgraded && upgrade ? upgrade : item;
  const payOf = (it, offer) => (offer && it.offer_cents && it.offer_cents < it.price_cents ? it.offer_cents : it?.price_cents) || 0;
  const addonActive = addOnline && onlineItem && mainItem && onlineItem.id !== mainItem.id;
  const quoteLines = !mainItem ? [] : [
    { it: mainItem, orig: mainItem.price_cents, pay: payOf(mainItem, upgraded) },
    ...(addonActive ? [{ it: onlineItem, orig: onlineItem.price_cents, pay: payOf(onlineItem, true) }] : []),
  ];
  const promoDiscount = (linesArr) => {
    if (!promo || promo === false) return 0;
    const scope = promo.applies_to_item_ids || [];
    const eligible = linesArr.reduce((s, l) => s + (!scope.length || scope.includes(l.it ? l.it.id : l.id) ? l.pay : 0), 0);
    if (eligible <= 0) return 0;
    return promo.discount_type === "fixed" ? Math.min(promo.discount_cents || 0, eligible)
      : Math.round(eligible * (promo.discount_percent || 0) / 100);
  };
  const discount = promoDiscount(quoteLines);
  const origTotal = quoteLines.reduce((s, l) => s + l.orig, 0);
  const payTotal = Math.max(0, quoteLines.reduce((s, l) => s + l.pay, 0) - discount);
  const cartDiscount = promoDiscount(cartLines.map((l) => ({ id: l.id, it: l.item, pay: l.item.price_cents * l.qty })));
  const cartPayTotal = Math.max(0, cartTotal - cartDiscount);
  const $ = (c) => `$${(c / 100).toFixed(2)}`;
  const headlineDiscount = item ? promoDiscount([{ it: item, pay: item.price_cents }]) : 0;
  const headlinePay = item ? Math.max(0, item.price_cents - headlineDiscount) : 0;

  const applyCode = async () => {
    const code = codeInput.trim();
    if (!code) { setPromo(null); return; }
    try {
      const { data } = await axios.get(`${API}/site/front-desk/resolve-code`, { params: { code } });
      setPromo(data.valid ? data : false);
    } catch { setPromo(false); }
  };

  const codeBox = (
    <div className="text-left mt-3" data-testid="buy-code-box">
      <div className="flex items-center gap-2">
        <input className="flex-1 rounded-lg px-3 py-2 border-0 bg-white text-[#000000] text-sm uppercase" value={codeInput}
          onChange={(e) => { setCodeInput(e.target.value); if (promo !== null) setPromo(null); }}
          onKeyDown={(e) => e.key === "Enter" && applyCode()}
          placeholder="Discount code (optional)" data-testid="buy-code-input" />
        <button onClick={applyCode} data-testid="buy-code-apply"
          className="rounded-lg px-3 py-2 text-xs font-bold border border-white/30 bg-transparent till-text cursor-pointer">Apply</button>
      </div>
      {promo === false && <div className="text-[#FF5100] text-xs mt-1" data-testid="buy-code-invalid">That code isn't valid or has expired.</div>}
      {promo && promo !== false && (
        <div className="text-[#9EF01A] text-xs mt-1" data-testid="buy-code-applied">
          Code {promo.code} applied{(isCart ? cartDiscount : discount) > 0 ? ` — you're saving ${$(isCart ? cartDiscount : discount)}` : " — doesn't apply to these items"}
        </div>
      )}
    </div>
  );

  useEffect(() => {
    axios.get(`${API}/site/front-desk/items`)
      .then((r) => { setAllItems(r.data || []); setItem((r.data || []).find((i) => i.id === itemId) || null); })
      .finally(() => setLoaded(true));
  }, [itemId]);

  const pay = async () => {
    setErr("");
    const digits = phone.replace(/\D/g, "");
    if (!email.includes("@") && digits.length < 10) { setErr("Enter your email or a 10-digit phone number."); return; }
    setBusy(true);
    try {
      const { data } = isCart
        ? await axios.post(`${API}/site/front-desk/cart-checkout`, { lines: cartLines.map((l) => ({ item_id: l.id, qty: l.qty })), name, email, phone, code: promo && promo !== false ? promo.code : "" })
        : await axios.post(`${API}/site/front-desk/checkout`, { item_id: mainItem.id, upsell: upgraded, addon_item_ids: addonActive ? [onlineItem.id] : [], name, email, phone, code: promo && promo !== false ? promo.code : "" });
      window.location.href = data.url;
    } catch (e) {
      if (e?.response?.status === 403) {
        const thenUrl = isCart ? `/buy/cart?i=${encodeURIComponent(iParam)}` : `/buy/${itemId}`;
        nav(`/register?then=${encodeURIComponent(thenUrl)}&email=${encodeURIComponent(email)}&phone=${encodeURIComponent(phone)}&name=${encodeURIComponent(name)}`);
        return;
      }
      setErr(e?.response?.data?.detail || "Could not start checkout — please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-grain flex items-start justify-center p-4 pt-8">
      <div className="w-full max-w-lg rounded-2xl bg-[#101318] p-6 shadow-2xl" data-testid="buy-page">
        <div className="text-center mb-5">
          <div className="text-xs uppercase tracking-[0.3em] till-amber font-display mb-1">The Self-Agency Project</div>
          {!loaded ? <p className="text-white/60">Loading…</p> : isCart ? (
            cartLines.length > 0 ? (
              <div data-testid="buy-cart-summary">
                <h1 className="text-2xl font-extrabold font-display till-text m-0 mb-1">Your order</h1>
                <div className="flex flex-col gap-1.5 text-left mt-3">
                  {cartLines.map((l) => (
                    <div key={l.id} className="flex items-center justify-between gap-3" data-testid={`buy-cart-line-${l.id}`}>
                      <span className="till-text text-sm">{l.qty} × {l.item.name}</span>
                      <span className="till-accent font-display font-bold shrink-0">${((l.item.price_cents * l.qty) / 100).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between border-t border-white/15 mt-3 pt-2">
                  <span className="till-text font-display font-bold">Total</span>
                  <span className="text-right" data-testid="buy-cart-total">
                    {cartPayTotal < cartTotal && <s className="text-white/40 text-lg mr-2">{$(cartTotal)}</s>}
                    <b className="text-3xl font-extrabold font-display till-accent">{$(cartPayTotal)}</b>
                    {cartPayTotal < cartTotal && <span className="block text-[#9EF01A] text-xs font-bold">You save {$(cartTotal - cartPayTotal)}</span>}
                  </span>
                </div>
                {codeBox}
              </div>
            ) : (
              <p className="text-white/70" data-testid="buy-not-found">This order link is empty — ask at the front desk.</p>
            )
          ) : !item ? (
            allItems.length > 0 ? (
              <div data-testid="buy-item-chooser">
                <h1 className="text-2xl font-extrabold font-display till-text m-0 mb-1">Purchase Training</h1>
                <p className="text-white/60 text-sm mb-4">Pick what you'd like:</p>
                <div className="flex flex-col gap-2 text-left">
                  {allItems.map((it) => (
                    <button key={it.id} data-testid={`buy-choose-${it.id}`}
                      onClick={() => nav(`/buy/${it.id}?email=${encodeURIComponent(email)}&phone=${encodeURIComponent(phone)}&name=${encodeURIComponent(name)}`)}
                      className="w-full rounded-xl px-4 py-3 border border-white/20 bg-[#181C26] cursor-pointer flex items-center justify-between gap-3 hover:border-[#FFE01A]">
                      <span className="till-text font-display font-bold text-sm">
                        {it.name}{it.sessions > 0 ? ` · ${it.sessions} sessions` : ""}
                        {it.course_duration_minutes > 0 && (
                          <span className="block font-sans font-normal text-xs text-white/50 mt-0.5" data-testid={`buy-choose-duration-${it.id}`}>
                            {it.course_lesson_count} lessons · ~{it.course_duration_minutes >= 60 ? `${Math.round(it.course_duration_minutes / 60)} hours` : `${it.course_duration_minutes} min`} of training
                          </span>
                        )}
                      </span>
                      <span className="till-accent font-display font-extrabold shrink-0">${(it.price_cents / 100).toFixed(0)}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-white/70" data-testid="buy-not-found">This item is no longer available — ask at the front desk.</p>
            )
          ) : (
            <>
              <h1 className="text-2xl font-extrabold font-display till-text m-0" data-testid="buy-item-name">{item.name}</h1>
              {item.description && <p className="text-white/60 text-sm mt-1">{item.description}</p>}
              <div className="text-4xl font-extrabold font-display till-accent mt-2" data-testid="buy-item-price">
                {headlinePay < item.price_cents && <s className="text-white/40 text-2xl mr-2 font-normal">{$(item.price_cents)}</s>}
                {$(headlinePay)}
              </div>
              {headlinePay < item.price_cents && (
                <div className="text-[#9EF01A] text-sm font-bold mt-0.5" data-testid="buy-item-savings">You save {$(item.price_cents - headlinePay)}</div>
              )}
              {item.sessions > 0 && <div className="text-white/50 text-xs mt-0.5">{item.sessions}-session pack — we track your remaining sessions for you</div>}
              {item.course_duration_minutes > 0 && (
                <div className="text-white/60 text-sm mt-1.5" data-testid="buy-item-duration">
                  Includes the full online course — {item.course_lesson_count} lessons, ~{item.course_duration_minutes >= 60 ? `${Math.round(item.course_duration_minutes / 60)} hours` : `${item.course_duration_minutes} minutes`} of training
                </div>
              )}
              {codeBox}
              {(upgrade || (onlineItem && onlineItem.id !== item.id)) && (
                <div className="mt-4 text-left flex flex-col gap-2" data-testid="buy-upsell-section">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-white/50">While you're here — today's pricing</div>
                  {upgrade && (
                    <label className={`rounded-xl border p-3 flex items-start gap-2.5 cursor-pointer ${upgraded ? "border-[#FFE01A] bg-[#1C2014]" : "border-white/20 bg-[#181C26]"}`} data-testid="buy-upsell-upgrade">
                      <input type="checkbox" className="mt-1" checked={upgraded} onChange={(e) => setUpgraded(e.target.checked)} data-testid="buy-upsell-upgrade-check" />
                      <span className="text-sm till-text">
                        <b className="font-display">Upgrade to {upgrade.name}</b>
                        {upgrade.description && <span className="block text-xs text-white/60 mt-0.5">{upgrade.description}</span>}
                        <PriceTag orig={upgrade.price_cents} pay={payOf(upgrade, true)} />
                      </span>
                    </label>
                  )}
                  {onlineItem && onlineItem.id !== (upgraded && upgrade ? upgrade.id : item.id) && (
                    <label className={`rounded-xl border p-3 flex items-start gap-2.5 cursor-pointer ${addOnline ? "border-[#FFE01A] bg-[#1C2014]" : "border-white/20 bg-[#181C26]"}`} data-testid="buy-upsell-online">
                      <input type="checkbox" className="mt-1" checked={addOnline} onChange={(e) => setAddOnline(e.target.checked)} data-testid="buy-upsell-online-check" />
                      <span className="text-sm till-text">
                        <b className="font-display">Add {onlineItem.name}</b>
                        {onlineItem.description && <span className="block text-xs text-white/60 mt-0.5">{onlineItem.description}</span>}
                        <PriceTag orig={onlineItem.price_cents} pay={payOf(onlineItem, true)} />
                      </span>
                    </label>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {(item || cartLines.length > 0) && (
          <>
            <label className="block text-xs text-white/70 mb-1">Name (optional)</label>
            <input className="w-full rounded-lg px-3 py-2.5 mb-3 border-0 bg-white text-[#000000]" value={name}
              onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" data-testid="buy-name-input" />
            <label className="block text-xs text-white/70 mb-1">Email or mobile — we check your registration & send your receipt</label>
            <input className="w-full rounded-lg px-3 py-2.5 mb-2 border-0 bg-white text-[#000000]" value={email} inputMode="email"
              onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="buy-email-input" />
            <input className="w-full rounded-lg px-3 py-2.5 mb-3 border-0 bg-white text-[#000000]" value={phone} inputMode="tel"
              onChange={(e) => setPhone(e.target.value)} placeholder="403-555-0123" data-testid="buy-phone-input" />
            {err && <div className="text-[#FF5100] text-xs mb-2" data-testid="buy-error">{err}</div>}
            <div className="rounded-xl bg-[#181C26] p-3 mb-3 text-left" data-testid="buy-summary">
              {!isCart && quoteLines.map((l) => (
                <div key={l.it.id} className="flex items-center justify-between gap-3 text-sm mb-1" data-testid={`buy-summary-line-${l.it.id}`}>
                  <span className="till-text">{l.it.name}</span>
                  <span className="shrink-0">
                    {l.pay < l.orig && <s className="text-white/40 mr-1.5">{$(l.orig)}</s>}
                    <b className="till-accent font-display">{$(l.pay)}</b>
                  </span>
                </div>
              ))}
              {promo && promo !== false && (isCart ? cartDiscount : discount) > 0 && (
                <div className="flex items-center justify-between gap-3 text-sm mb-1 text-[#9EF01A]" data-testid="buy-summary-discount">
                  <span>Code {promo.code}</span>
                  <span className="font-bold shrink-0">−{$(isCart ? cartDiscount : discount)}</span>
                </div>
              )}
              <div className="border-t border-white/15 mt-2 pt-2 flex items-center justify-between">
                <span className="till-text font-display font-bold">Total</span>
                <span className="text-right" data-testid="buy-final-total">
                  {(isCart ? cartPayTotal < cartTotal : payTotal < origTotal) && <s className="text-white/40 mr-2">{$(isCart ? cartTotal : origTotal)}</s>}
                  <b className="text-2xl font-extrabold font-display till-accent">{$(isCart ? cartPayTotal : payTotal)}</b>
                  {(isCart ? cartTotal - cartPayTotal : origTotal - payTotal) > 0 && (
                    <span className="block text-[#9EF01A] text-xs font-bold" data-testid="buy-total-savings">
                      You save {$(isCart ? cartTotal - cartPayTotal : origTotal - payTotal)}
                    </span>
                  )}
                </span>
              </div>
            </div>
            <button onClick={pay} disabled={busy} data-testid="buy-pay-btn"
              className="w-full py-3 rounded-full font-display font-bold uppercase tracking-wide text-sm border-0 cursor-pointer disabled:opacity-60"
              style={{ background: "#FFE01A", color: "#000000" }}>
              {busy ? "Starting checkout…" : "Continue to secure payment"}
            </button>
            <p className="text-[10px] text-white/40 mt-2 text-center">Registration (signed waiver) is required before purchase — we'll walk you through it if you haven't signed yet. Payments by Stripe.</p>
          </>
        )}
      </div>
    </div>
  );
}
